import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment-timezone';
import { FiArrowLeft, FiExternalLink, FiSend, FiCheck, FiFileText, FiEdit2, FiEye, FiBarChart2, FiClock, FiAlertCircle, FiMail, FiLoader } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { CommunicationsAnnouncementResult } from '../../../model/CommunicationsAnnouncementsResult';
import { GetAnnouncementEditorHtmlResult } from '../../../model/GetAnnouncementEditorHtmlResult';
import { Announcement } from '../../../model/Stock';
import api, { combinedTicker } from '../../../api/api';
import { to, pluralize } from '../../../util/util';
import { toast } from '../Toast';
import ConfirmEx from '../../ConfirmEx';
import { getExternalLink } from '../../stock/AnnouncementTable';
import AnnouncementEditForm from './AnnouncementEditForm';
import AnnouncementDetails from './AnnouncementDetails';

interface Props {
    sendGuid: string;
    view: AnnouncementView; // Details (2:57821) and Edit (2:58009) share this page's header and left nav
    onChanged?: () => void; // refetch the sub-tab badge counts
}

export type AnnouncementView = 'details' | 'edit' | 'preview';
type View = 'Desktop' | 'Mobile' | 'Plain Text';

const providerNames: { [key: string]: string } = { Mailchimp: 'Mailchimp', CampaignMonitor: 'Campaign Monitor' };

// block-level closers become line breaks so the plain-text view keeps the email's paragraphs
const plainText = (html: string): string => {
    const doc = new DOMParser().parseFromString(html.replace(/<br\s*\/?>|<\/(p|div|tr|li|h[1-6]|table)>/gi, m => `${m}\n`), 'text/html');
    doc.querySelectorAll('style, script, title').forEach(x => x.remove());
    return (doc.body.textContent || '').split('\n').map(x => x.replace(/\s+/g, ' ').trim()).join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

const status = (badge: string) => {
    if (badge === 'Emailed') return { pill: 'Emailed to investors', tone: 'ok', icon: <FiCheck />, text: 'Sent to investors' };
    if (badge === 'Confirm to send') return { pill: 'Email waiting for approval', tone: 'warn', icon: <FiClock />, text: 'Waiting for your approval' };
    return { pill: 'Not emailed', tone: '', icon: <FiMail />, text: 'Not emailed yet' };
};

// spec 2:58205. Statistics not built yet; Edit Template omitted (Website Hub is out of scope).
const AnnouncementEmailPreview: React.FC<Props> = ({ sendGuid, view: page, onChanged }) => {
    const history = useHistory();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const user = useSelector((state: IGlobalState) => state.user);
    const [detail, setDetail] = useState<CommunicationsAnnouncementResult | null>(null);
    const [error, setError] = useState('');
    const [email, setEmail] = useState<GetAnnouncementEditorHtmlResult | null>(null);
    const [view, setView] = useState<View>('Desktop');
    const [frameHeight, setFrameHeight] = useState(900);
    const [sending, setSending] = useState(false);
    const [testing, setTesting] = useState(false);
    const [reload, setReload] = useState(0);
    const ticker = stock ? combinedTicker(stock.ticker, stock.exchange) : '';

    useAsyncEffect(async isMounted => {
        if (!stock?.id) return;
        const [err, data] = await to(api().getCommunicationsAnnouncement(stock.id, sendGuid));
        if (!isMounted()) return;
        if (err) setError(err.message || 'Failed to load the announcement');
        else { setError(''); setDetail(data); }
    }, [stock?.id, sendGuid, reload]);

    // sent: the stored campaign verbatim, never a fresh render. Unsent: slow, the server renders the campaign via a preview event
    useAsyncEffect(async isMounted => {
        if (!ticker || !detail || page !== 'preview') return;
        if (detail.row.badge === 'Emailed') {
            setEmail(detail.sentHtml ? { success: true, html: detail.sentHtml, subject: detail.sentSubject || '' } as GetAnnouncementEditorHtmlResult : null);
            return;
        }
        setEmail(null);
        const [err, data] = await to(api().getAnnouncementEditorHtml(ticker, sendGuid));
        if (!isMounted()) return;
        setEmail(err ? { success: false, errorMessage: err.message } as GetAnnouncementEditorHtmlResult : data);
    }, [ticker, sendGuid, detail?.row.badge, detail?.sentHtml, page]);

    const back = <button type="button" className="link back" onClick={() => history.push('/communications')}><FiArrowLeft /> Back to announcements</button>;
    if (error) return <div className="announcement-preview">{back}<p className="error">{error}</p></div>;
    if (!detail) return <div className="announcement-preview">{back}<p className="loading">Loading announcement…</p></div>;

    const { row } = detail;
    const sent = row.badge === 'Emailed';
    const s = status(row.badge);
    const released = moment(row.date).tz('Australia/Sydney');

    const approve = async () => {
        if (!await ConfirmEx(`Email “${row.headline}” to ${detail.recipients.toLocaleString()} investors now?`)) return;
        setSending(true);
        const announcement = { sendGuid: row.sendGuid, headline: row.headline } as Announcement;
        const result = await api().sendAnnouncement(ticker, announcement, false).catch(e => ({ success: false, errorMessage: e.message }));
        setSending(false);
        if (!result.success) { toast.error(`Send failed: ${result.errorMessage}`); return; }
        toast.success('Email sent to investors');
        setReload(x => x + 1);
        onChanged?.();
    };

    const sendTest = async () => {
        if (!email?.success) return;
        setTesting(true);
        const result = await api().sendAnnouncementEditorPreview(ticker, sendGuid, email.html, email.subject, user.username).catch(e => ({ success: false, errorMessage: e.message }));
        setTesting(false);
        if (result.success) toast.success(`Test sent to ${user.username}`);
        else toast.error(`Test failed: ${result.errorMessage}`);
    };

    const fitFrame = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
        const body = e.currentTarget.contentDocument?.body;
        if (body) setFrameHeight(body.scrollHeight + 24);
    };

    const base = `/communications/announcement/${sendGuid}`;
    const navItem = (icon: React.ReactNode, title: string, sub: string, active = false, to?: string) =>
        <button type="button" className={`nav-item${active ? ' active' : ''}`} disabled={!active && !to} title={active || to ? undefined : 'Coming soon'}
            onClick={() => to && !active && history.push(to)}>
            <span className="icon">{icon}</span>
            <span className="t"><strong>{title}</strong><span>{sub}</span></span>
        </button>;

    // sent but no stored campaign: the copy lives with the external provider (or predates stored copies)
    const notStored = () => {
        const last = detail.sends[detail.sends.length - 1];
        const provider = providerNames[last?.provider];
        return <div className="not-stored">
            <FiMail />
            <p>{provider
                ? `This email was sent through ${provider}, so ${provider} holds the copy of what went out — Investi doesn't keep one.`
                : 'This email was sent through Mailchimp or Campaign Monitor, so Investi doesn\'t keep a copy.'}</p>
            {provider && last.campaignUrl && <a className="btn-outline" href={last.campaignUrl} target="_blank" rel="noopener noreferrer"><FiExternalLink /> View it in {provider}</a>}
        </div>;
    };

    const stage = () => {
        if (sent && !detail.sentHtml) return notStored();
        if (!email) return <p className="loading"><FiLoader className="spin" /> Building the email preview…</p>;
        if (!email.success) return <p className="error">{email.errorMessage || 'Preview failed'}</p>;
        return <>
            <div className="envelope">
                {detail.fromEmail && <div className="from">From: {detail.fromName} &lt;{detail.fromEmail}&gt;</div>}
                <div className="subject">Subject: {email.subject}</div>
            </div>
            {view === 'Plain Text'
                ? <pre className="plain">{plainText(email.html)}</pre>
                : <iframe title="Email preview" className={view === 'Mobile' ? 'mobile' : ''} srcDoc={email.html} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    style={{ height: frameHeight }} onLoad={fitFrame} />}
        </>;
    };

    return <div className="announcement-preview">
        {back}
        <div className="title-row">
            <div>
                <h1>{row.headline}</h1>
                <div className="meta">
                    {row.priceSensitive && <span className="pill bad">Price sensitive</span>}
                    <span className={`pill ${s.tone}`}>{s.pill}</span>
                    <span>Released {released.format('D MMM YYYY, h:mm a')} Sydney · PDF{row.pages > 0 && `, ${pluralize('page', row.pages)}`}</span>
                </div>
            </div>
            <div className="actions">
                <a className="btn-outline" href={getExternalLink({ localPath: row.localPath })} target="_blank" rel="noopener noreferrer"><FiExternalLink /> Open on the Website</a>
                {sent
                    ? <button type="button" className="btn-primary" disabled><FiCheck /> Sent</button>
                    : <button type="button" className="btn-primary" onClick={approve} disabled={sending}><FiSend /> {sending ? 'Sending…' : 'Approve and Send'}</button>}
            </div>
        </div>

        <div className="layout">
            <div className="card side-nav">
                <div className="label">This announcement</div>
                {navItem(<FiFileText />, 'Details', 'What was released', page === 'details', base)}
                {navItem(<FiEdit2 />, 'Edit', 'Fix the record', page === 'edit', `${base}/edit`)}
                {navItem(<FiEye />, 'Email Preview', sent ? 'The email that was sent' : 'See the email', page === 'preview', `${base}/preview`)}
                {navItem(<FiBarChart2 />, 'Statistics', 'Views, likes, questions')}
                <div className="email-status">
                    <div className="label">Email status</div>
                    <div className={`status ${s.tone}`}>{s.icon} {s.text}</div>
                </div>
            </div>

            {page === 'details' && <AnnouncementDetails detail={detail} />}
            {page === 'edit' && <AnnouncementEditForm key={`${row.headline}|${row.date}|${row.tags.join('|')}`} detail={detail}
                    onSaved={() => { setReload(x => x + 1); onChanged?.(); }}
                    onCancel={() => history.push(base)}
                    onDeleted={() => { onChanged?.(); history.push('/communications'); }} />}
            {page === 'preview' && <div className="main">
                <div className="card views">
                    {(['Desktop', 'Mobile', 'Plain Text'] as View[]).map(v =>
                        <button key={v} type="button" className={v === view ? 'active' : ''} onClick={() => setView(v)}>{v}</button>)}
                </div>
                <div className="body">
                    <div className="stage">{stage()}</div>
                    <div className="card before-send">
                        <h2>{sent ? 'Sent' : 'Before you send'}</h2>
                        <dl>
                            {sent
                                ? detail.sends.map((x, i) => <React.Fragment key={i}>
                                    <dt>{i === 0 ? 'Sent' : 'Re-sent'}</dt>
                                    <dd>
                                        {moment(x.timestamp).format('D MMM YYYY, h:mm a')}
                                        <div className="by">{x.sentBy === '[Auto-Send]' ? 'Auto-send' : `by ${x.sentBy}`}</div>
                                        {x.campaignUrl && <a href={x.campaignUrl} target="_blank" rel="noopener noreferrer">View campaign</a>}
                                    </dd>
                                </React.Fragment>)
                                : <><dt>Goes to</dt><dd>{detail.recipients.toLocaleString()} investors</dd></>}
                            <dt>From</dt><dd>{detail.fromEmail || '—'}</dd>
                            <dt>Template</dt><dd>{detail.templateName || 'Default'}</dd>
                            <dt>The PDF</dt><dd>Linked</dd>
                            <dt>Unsubscribe</dt><dd>In the footer</dd>
                        </dl>
                        {row.priceSensitive && !sent && <div className="note"><FiAlertCircle /> This is price sensitive. It went to the ASX at {released.format('h:mm a')} Sydney time, so you are free to email it now.</div>}
                        <button type="button" className="btn-outline" onClick={sendTest} disabled={!email?.success || testing}><FiMail /> {testing ? 'Sending…' : 'Send a Test to Me'}</button>
                    </div>
                </div>
            </div>}
        </div>
    </div>;
};

export default AnnouncementEmailPreview;
